import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import PageWrapper from "../components/PageWrapper";
import "./TimeOffConfigurationPage.css";
import { useSiteContext } from "../SiteContext";
import { Page } from "../interfaces/PagesInterface";
import config from "../configLoader";
import * as TimeOffAPI from "../api/timeOff";
import {
    TimeOffCategory,
    TimeOffPolicySummary,
    TimeOffPolicyDetail,
    TimeOffPolicyCategoryDetail,
    TimeOffPolicyTier,
    SeniorityDateSource,
    TierAdvancementTiming,
    BalanceSource,
    AccrualMethod,
    CreateTimeOffPolicyBody,
    PatchTimeOffPolicyBody,
    AddPolicyTierBody,
    AddPolicyCategoryBody,
    PatchPolicyCategoryBody,
    HolidayCalendarEntry,
    HolidayPolicyDetail,
} from "../interfaces/TimeOff";
import {
    Sliders,
    Settings,
    Layers,
    Plus,
    Pencil,
    Ban,
    RotateCcw,
    Trash2,
    CalendarClock,
    Coins,
    Gauge,
    UserCheck,
    AlertTriangle,
    Check,
    CheckCircle,
    ChevronDown,
    ChevronRight,
    X,
    Calendar,
    CalendarCheck,
    Search,
    Inbox,
} from "lucide-react";

type TabId = "categories" | "policies" | "holidayCalendar" | "holidayPolicies";

// ─── Enum human labels (raw enum values stay as select option VALUES) ─────────
const LABELS = {
    seniorityDateSource: { HIRE_DATE: "Hire date", CUSTOM_DATE: "Custom date" } as Record<SeniorityDateSource, string>,
    tierAdvancementTiming: {
        CURRENT_PERIOD: "Current period",
        NEXT_PERIOD: "Next period",
        NEXT_CALENDAR_YEAR: "Next calendar year",
    } as Record<TierAdvancementTiming, string>,
    balanceSource: { INTERNAL: "Internal (tracked here)", EXTERNAL: "External system" } as Record<BalanceSource, string>,
    accrualMethod: {
        PER_PERIOD: "Per period",
        LUMP_SUM: "Lump sum",
        LUMP_SUM_AND_PER_PERIOD: "Lump sum + per period",
    } as Record<AccrualMethod, string>,
};

const SENIORITY_OPTS: SeniorityDateSource[] = ["HIRE_DATE", "CUSTOM_DATE"];
const TIER_TIMING_OPTS: TierAdvancementTiming[] = ["CURRENT_PERIOD", "NEXT_PERIOD", "NEXT_CALENDAR_YEAR"];
const BALANCE_OPTS: BalanceSource[] = ["INTERNAL", "EXTERNAL"];
const ACCRUAL_OPTS: AccrualMethod[] = ["PER_PERIOD", "LUMP_SUM", "LUMP_SUM_AND_PER_PERIOD"];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** "YYYY-MM-DD" (or ISO timestamp) → "MM/DD/YYYY". */
function fmtDate(ymd: string): string {
    if (!ymd) return "—";
    const [y, m, d] = ymd.slice(0, 10).split("-");
    return `${m}/${d}/${y}`;
}

function fmtNum(n: number): string {
    return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(2)));
}

/** Hours label, e.g. 40 → "40 h", 37.5 → "37.5 h", null → "—". */
function hoursLabel(n: number | null | undefined): string {
    if (n == null) return "—";
    return `${fmtNum(n)} h`;
}

const PALETTE = ["c-sick", "c-vacation", "c-pto"] as const;

/** Map a category to its color class — Sick/Vacation/PTO by name, else stable by id. */
function categoryColorClass(name: string, id?: number): string {
    const n = (name ?? "").toLowerCase();
    if (n.includes("sick")) return "c-sick";
    if (n.includes("vacation")) return "c-vacation";
    if (n.includes("pto") || n.includes("personal")) return "c-pto";
    if (n.includes("bereavement")) return "c-vacation";
    if (id != null) return PALETTE[(((id % PALETTE.length) + PALETTE.length) % PALETTE.length)];
    return "c-pto";
}

// ─── Small presentational subcomponents ───────────────────────────────────────

const StatusPill: React.FC<{ active: boolean }> = ({ active }) => (
    <span className={`toc-pill ${active ? "active" : "inactive"}`}>
        <span className="toc-pill-dot" />
        {active ? "Active" : "Inactive"}
    </span>
);

const CategoryChip: React.FC<{ name: string; id?: number }> = ({ name, id }) => (
    <span className={`toc-chip ${categoryColorClass(name, id)}`}>
        <span className="toc-chip-dot" />
        {name}
    </span>
);

/** One of the three colored "hours" stat chips on a category block header. */
const HStat: React.FC<{
    variant: "annual" | "cashout" | "cap";
    icon: React.ReactNode;
    label: string;
    value: string;
    muted?: boolean;
}> = ({ variant, icon, label, value, muted }) => (
    <span className={`toc-hstat ${variant}`}>
        <span className="hs-ic">{icon}</span>
        <span className="hs-tx">
            <span className="hs-k">{label}</span>
            <span className={`hs-v${muted ? " unl" : ""}`}>{value}</span>
        </span>
    </span>
);

const Toggle: React.FC<{ checked: boolean; onChange: () => void; label?: string }> = ({
    checked,
    onChange,
    label = "Active only",
}) => (
    <label className="toc-switch">
        <input type="checkbox" checked={checked} onChange={onChange} />
        <span className="toc-track" />
        {label}
    </label>
);

// ─── Floating select (overlays the modal, per fidelity note) ───────────────────

interface FloatingOption {
    value: string;
    label: string;
    dotClass?: string;
}

const FloatingSelect: React.FC<{
    value: string;
    options: FloatingOption[];
    onChange: (v: string) => void;
    placeholder?: string;
}> = ({ value, options, onChange, placeholder }) => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState<{ left: number; top: number; width: number }>({ left: 0, top: 0, width: 0 });
    const btnRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const selected = options.find((o) => o.value === value);

    const openMenu = () => {
        const el = btnRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setPos({ left: r.left, top: r.bottom + 6, width: Math.max(r.width, 200) });
        setOpen(true);
    };

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(e.target as Node) &&
                btnRef.current &&
                !btnRef.current.contains(e.target as Node)
            ) {
                setOpen(false);
            }
        };
        const onEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDoc, true);
        document.addEventListener("keydown", onEsc);
        return () => {
            document.removeEventListener("mousedown", onDoc, true);
            document.removeEventListener("keydown", onEsc);
        };
    }, [open]);

    // clamp above the trigger if it would overflow the viewport bottom
    useLayoutEffect(() => {
        if (open && menuRef.current && btnRef.current) {
            const mr = menuRef.current.getBoundingClientRect();
            if (mr.bottom > window.innerHeight - 10) {
                const r = btnRef.current.getBoundingClientRect();
                setPos((p) => ({ ...p, top: Math.max(10, r.top - mr.height - 6) }));
            }
        }
    }, [open]);

    return (
        <>
            <button
                type="button"
                ref={btnRef}
                className="toc-input toc-fs-trigger"
                onClick={() => (open ? setOpen(false) : openMenu())}
            >
                {selected ? (
                    <span className="toc-fs-inner">
                        {selected.dotClass && <span className={`toc-fs-dot ${selected.dotClass}`} />}
                        {selected.label}
                    </span>
                ) : (
                    <span className="toc-fs-ph">{placeholder || "Select…"}</span>
                )}
                <ChevronDown size={15} />
            </button>
            {open && (
                <div
                    ref={menuRef}
                    className="toc-fmenu"
                    style={{ left: pos.left, top: pos.top, minWidth: pos.width }}
                >
                    {options.map((o) => (
                        <div
                            key={o.value}
                            className={`toc-fm-item${o.value === value ? " sel" : ""}`}
                            onClick={() => {
                                onChange(o.value);
                                setOpen(false);
                            }}
                        >
                            {o.dotClass && <span className={`toc-fs-dot ${o.dotClass}`} />}
                            <span>{o.label}</span>
                            {o.value === value && <Check size={15} />}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

// ─── Confirm modal ─────────────────────────────────────────────────────────────

interface ConfirmState {
    variant: "danger" | "warn";
    icon: React.ReactNode;
    title: string;
    sub?: string;
    body?: React.ReactNode;
    confirmLabel: string;
    confirmIcon: React.ReactNode;
    onConfirm: () => void;
}

const ConfirmModal: React.FC<{ state: ConfirmState; onClose: () => void }> = ({ state, onClose }) => {
    useEffect(() => {
        const onEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [onClose]);

    return (
        <div className="toc-modal-bg" onClick={onClose}>
            <div className="toc-modal" onClick={(e) => e.stopPropagation()}>
                <div className={`toc-modal-head ${state.variant}`}>
                    <span className="mi">{state.icon}</span>
                    <div>
                        <h3>{state.title}</h3>
                        {state.sub && <div className="ms">{state.sub}</div>}
                    </div>
                </div>
                {state.body && <div className="toc-modal-body">{state.body}</div>}
                <div className="toc-modal-foot">
                    <button className="toc-btn ghost" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className={`toc-btn ${state.variant === "warn" ? "primary" : "danger-solid"}`}
                        onClick={state.onConfirm}
                    >
                        {state.confirmIcon}
                        {state.confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Modal shell (form) ────────────────────────────────────────────────────────

const FormModal: React.FC<{
    icon: React.ReactNode;
    title: string;
    sub?: string;
    wide?: boolean;
    onClose: () => void;
    onSubmit: (e: React.FormEvent) => void;
    saveLabel: string;
    children: React.ReactNode;
}> = ({ icon, title, sub, wide, onClose, onSubmit, saveLabel, children }) => {
    useEffect(() => {
        const onEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [onClose]);

    return (
        <div className="toc-modal-bg" onClick={onClose}>
            <div className={`toc-modal form${wide ? " wide" : ""}`} onClick={(e) => e.stopPropagation()}>
                <form onSubmit={onSubmit}>
                    <div className="toc-m-head">
                        <span className="m-ic">{icon}</span>
                        <div>
                            <h3>{title}</h3>
                            {sub && <div className="m-sub">{sub}</div>}
                        </div>
                        <button type="button" className="m-close" onClick={onClose}>
                            <X size={16} />
                        </button>
                    </div>
                    <div className="toc-m-body">
                        <div className="toc-fgrid">{children}</div>
                    </div>
                    <div className="toc-m-foot">
                        <button type="button" className="toc-btn ghost" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="toc-btn primary">
                            <Check size={16} />
                            {saveLabel}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

/** Labeled form row with optional required/optional markers + hint. */
const FRow: React.FC<{
    label: string;
    required?: boolean;
    optional?: string;
    full?: boolean;
    hint?: React.ReactNode;
    children: React.ReactNode;
}> = ({ label, required, optional, full, hint, children }) => (
    <div className={`toc-frow${full ? " full" : ""}`}>
        <label>
            {label}
            {required && <span className="req">*</span>}
            {optional && <span className="opt">{optional}</span>}
        </label>
        {children}
        {hint && <div className="hint">{hint}</div>}
    </div>
);

/** Numeric input with a trailing unit suffix. */
const UnitInput: React.FC<{
    value: string;
    onChange: (v: string) => void;
    unit: string;
    placeholder?: string;
    step?: string;
    min?: string;
}> = ({ value, onChange, unit, placeholder, step, min }) => (
    <div className="toc-inwrap">
        <input
            className="toc-input"
            type="number"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            step={step}
            min={min}
        />
        <span className="suffix">{unit}</span>
    </div>
);

const FSection: React.FC<{ title: string }> = ({ title }) => (
    <div className="toc-fsection">
        <span className="t">{title}</span>
        <span className="ln" />
    </div>
);

// ─── Main page ─────────────────────────────────────────────────────────────────

const TimeOffConfigurationPage: React.FC = () => {
    const { setLoading, createPopup, userRole, setPage, translator } = useSiteContext();
    const [tab, setTab] = useState<TabId>("categories");

    // toast
    const [toast, setToast] = useState<string | null>(null);
    const toastTimer = useRef<number | null>(null);
    const showToast = useCallback((msg: string) => {
        setToast(msg);
        if (toastTimer.current) window.clearTimeout(toastTimer.current);
        toastTimer.current = window.setTimeout(() => setToast(null), 2200);
    }, []);
    useEffect(() => () => { if (toastTimer.current) window.clearTimeout(toastTimer.current); }, []);

    // confirm modal
    const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);

    // categories
    const [categories, setCategories] = useState<TimeOffCategory[] | null>(null);
    const [catActiveOnly, setCatActiveOnly] = useState(false);
    const [catSearch, setCatSearch] = useState("");
    const [catModalOpen, setCatModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState<TimeOffCategory | null>(null);
    const [catName, setCatName] = useState("");
    const [catDescription, setCatDescription] = useState("");
    const [catSortOrder, setCatSortOrder] = useState("0");
    const [catIsUnpaid, setCatIsUnpaid] = useState(false);
    const [catCountsHoliday, setCatCountsHoliday] = useState(true);

    // policies
    const [policies, setPolicies] = useState<TimeOffPolicySummary[] | null>(null);
    const [polActiveOnly, setPolActiveOnly] = useState(false);
    const [polSearch, setPolSearch] = useState("");
    const [selectedPolicyId, setSelectedPolicyId] = useState<number | null>(null);
    const [policyDetail, setPolicyDetail] = useState<TimeOffPolicyDetail | null>(null);
    const [openCats, setOpenCats] = useState<Set<number>>(new Set());
    const initedPolicyRef = useRef<number | null>(null);

    const [policyModalOpen, setPolicyModalOpen] = useState(false);
    const [policyModalMode, setPolicyModalMode] = useState<"create" | "edit">("create");
    const [pName, setPName] = useState("");
    const [pDescription, setPDescription] = useState("");
    const [pSeniority, setPSeniority] = useState<SeniorityDateSource>("HIRE_DATE");
    const [pTierTiming, setPTierTiming] = useState<TierAdvancementTiming>("NEXT_PERIOD");
    const [pLumpMonth, setPLumpMonth] = useState("");
    const [pLumpDay, setPLumpDay] = useState("");
    const [pBalance, setPBalance] = useState<BalanceSource>("INTERNAL");
    const [pHolidayPolicyId, setPHolidayPolicyId] = useState("");
    const [pHolidayEligibilityDays, setPHolidayEligibilityDays] = useState("");

    // policy category (add/edit)
    const [pcModalOpen, setPcModalOpen] = useState(false);
    const [pcModalMode, setPcModalMode] = useState<"add" | "edit">("add");
    const [editingPc, setEditingPc] = useState<TimeOffPolicyCategoryDetail | null>(null);
    const [pcCatId, setPcCatId] = useState("");
    const [pcCashOut, setPcCashOut] = useState(false);
    const [pcCashMax, setPcCashMax] = useState("");
    const [pcAnnualMax, setPcAnnualMax] = useState("");
    const [pcEligDays, setPcEligDays] = useState("");
    const [pcSort, setPcSort] = useState("0");
    const [pcRequiresPaidExhausted, setPcRequiresPaidExhausted] = useState(false);
    const [pcExhaustedBelow, setPcExhaustedBelow] = useState("");

    // tier
    const [tierModalOpen, setTierModalOpen] = useState(false);
    const [tierModalPcId, setTierModalPcId] = useState<number | null>(null);
    const [editingTier, setEditingTier] = useState<TimeOffPolicyTier | null>(null);
    const [tMinYrs, setTMinYrs] = useState("0");
    const [tAccrual, setTAccrual] = useState<AccrualMethod>("PER_PERIOD");
    const [tPerPeriod, setTPerPeriod] = useState("");
    const [tLumpSumH, setTLumpSumH] = useState("");
    const [tCap, setTCap] = useState("");
    const [tRollover, setTRollover] = useState(true);
    const [tSort, setTSort] = useState("0");

    // holiday calendar
    const [hcEntries, setHcEntries] = useState<HolidayCalendarEntry[] | null>(null);
    const [hcActiveOnly, setHcActiveOnly] = useState(false);
    const [hcSearch, setHcSearch] = useState("");
    const [hcModalOpen, setHcModalOpen] = useState(false);
    const [editingHc, setEditingHc] = useState<HolidayCalendarEntry | null>(null);
    const [hcDate, setHcDate] = useState("");
    const [hcName, setHcName] = useState("");
    const [hcPayCodeID, setHcPayCodeID] = useState("");
    const [hcPaidHours, setHcPaidHours] = useState("");
    const [hcRequireBefore, setHcRequireBefore] = useState(false);
    const [hcRequireAfter, setHcRequireAfter] = useState(false);
    const [hcRequireBeforeDate, setHcRequireBeforeDate] = useState("");
    const [hcRequireAfterDate, setHcRequireAfterDate] = useState("");
    const [hcStack, setHcStack] = useState(false);

    // holiday policies
    const [hpList, setHpList] = useState<HolidayPolicyDetail[] | null>(null);
    const [hpActiveOnly, setHpActiveOnly] = useState(false);
    const [hpSearch, setHpSearch] = useState("");
    const [selectedHpId, setSelectedHpId] = useState<number | null>(null);
    const [hpDetail, setHpDetail] = useState<HolidayPolicyDetail | null>(null);
    const [hpModalOpen, setHpModalOpen] = useState(false);
    const [editingHp, setEditingHp] = useState<HolidayPolicyDetail | null>(null);
    const [hpName, setHpName] = useState("");
    const [hpDesc, setHpDesc] = useState("");
    const [addHpHolModalOpen, setAddHpHolModalOpen] = useState(false);
    const [allHcForPicker, setAllHcForPicker] = useState<HolidayCalendarEntry[] | null>(null);
    const [pickerSelected, setPickerSelected] = useState<Set<number>>(new Set());

    // full category list for the policy attach dropdown (active global categories only)
    const [categoryOptions, setCategoryOptions] = useState<TimeOffCategory[] | null>(null);

    useEffect(() => {
        if (!config.showTimeOffConfig) {
            createPopup("Access Denied", "This feature is not available.");
            setPage(Page.Dashboard);
            return;
        }
        if (!userRole.admin) {
            createPopup("Access Denied", "You do not have permission to access this page.");
            setPage(Page.Dashboard);
        }
    }, [setPage, createPopup, userRole.admin]);

    /** Deps intentionally omit context functions — unstable refs recreate the callback every render and retrigger effects that depend on it (infinite loading). */
    const loadCategories = useCallback(async () => {
        try {
            setLoading(true);
            const data = await TimeOffAPI.getTimeOffCategories(catActiveOnly);
            setCategories(data);
        } catch (e) {
            console.error(e);
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(e));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- createPopup/setLoading unstable from SiteContext
    }, [catActiveOnly]);

    const loadPolicies = useCallback(async () => {
        try {
            setLoading(true);
            const data = await TimeOffAPI.getTimeOffPolicies(polActiveOnly);
            setPolicies(data);
        } catch (e) {
            console.error(e);
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(e));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- createPopup/setLoading unstable from SiteContext
    }, [polActiveOnly]);

    const loadPolicyDetail = useCallback(async (id: number) => {
        try {
            setLoading(true);
            const d = await TimeOffAPI.getTimeOffPolicyById(id);
            setPolicyDetail(d);
        } catch (e) {
            console.error(e);
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(e));
            setPolicyDetail(null);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- createPopup/setLoading unstable from SiteContext
    }, []);

    const loadHolidayCalendar = useCallback(async () => {
        try {
            setLoading(true);
            const data = await TimeOffAPI.getHolidayCalendar(hcActiveOnly);
            setHcEntries(data);
        } catch (e) {
            console.error(e);
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(e));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- createPopup/setLoading unstable from SiteContext
    }, [hcActiveOnly]);

    const loadHolidayPolicies = useCallback(async () => {
        try {
            setLoading(true);
            const data = await TimeOffAPI.getHolidayPolicies(hpActiveOnly);
            setHpList(data);
        } catch (e) {
            console.error(e);
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(e));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- createPopup/setLoading unstable from SiteContext
    }, [hpActiveOnly]);

    const loadHpDetail = useCallback(async (id: number) => {
        try {
            setLoading(true);
            const d = await TimeOffAPI.getHolidayPolicyById(id);
            setHpDetail(d);
        } catch (e) {
            console.error(e);
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(e));
            setHpDetail(null);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- createPopup/setLoading unstable from SiteContext
    }, []);

    useEffect(() => {
        if (!userRole.admin || !config.showTimeOffConfig) return;
        if (tab === "categories") loadCategories();
    }, [tab, userRole.admin, loadCategories]);

    useEffect(() => {
        if (!userRole.admin || !config.showTimeOffConfig) return;
        if (tab === "policies") loadPolicies();
    }, [tab, userRole.admin, loadPolicies]);

    // For the policies tab we need both the global category list (attach dropdown)
    // and the holiday-policy names (meta grid + policy modal picker).
    useEffect(() => {
        if (tab !== "policies" || !userRole.admin || !config.showTimeOffConfig) return;
        let cancelled = false;
        (async () => {
            try {
                const [cats, hps] = await Promise.all([
                    TimeOffAPI.getTimeOffCategories(false),
                    TimeOffAPI.getHolidayPolicies(false),
                ]);
                if (!cancelled) {
                    setCategoryOptions(cats);
                    setHpList(hps);
                }
            } catch (e) {
                console.error(e);
                if (!cancelled) setCategoryOptions([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [tab, userRole.admin]);

    useEffect(() => {
        if (selectedPolicyId != null) {
            loadPolicyDetail(selectedPolicyId);
        } else {
            setPolicyDetail(null);
        }
    }, [selectedPolicyId, loadPolicyDetail]);

    // first block open by default when a *different* policy loads (stable across reloads)
    useEffect(() => {
        if (policyDetail && initedPolicyRef.current !== policyDetail.timeOffPolicyID) {
            initedPolicyRef.current = policyDetail.timeOffPolicyID;
            const first = policyDetail.categories[0];
            setOpenCats(first ? new Set([first.timeOffPolicyCategoryID]) : new Set());
        }
    }, [policyDetail]);

    useEffect(() => {
        if (!userRole.admin || !config.showTimeOffConfig) return;
        if (tab === "holidayCalendar") loadHolidayCalendar();
    }, [tab, userRole.admin, loadHolidayCalendar]);

    useEffect(() => {
        if (!userRole.admin || !config.showTimeOffConfig) return;
        if (tab === "holidayPolicies") loadHolidayPolicies();
    }, [tab, userRole.admin, loadHolidayPolicies]);

    useEffect(() => {
        if (selectedHpId != null) {
            loadHpDetail(selectedHpId);
        } else {
            setHpDetail(null);
        }
    }, [selectedHpId, loadHpDetail]);

    // ─── Category handlers ────────────────────────────────────────────────────
    const openCreateCategory = () => {
        setEditingCategory(null);
        setCatName("");
        setCatDescription("");
        setCatSortOrder("0");
        setCatIsUnpaid(false);
        setCatCountsHoliday(true);
        setCatModalOpen(true);
    };

    const openEditCategory = (c: TimeOffCategory) => {
        setEditingCategory(c);
        setCatName(c.name);
        setCatDescription(c.description ?? "");
        setCatSortOrder(String(c.sortOrder ?? 0));
        // Missing flags (older API) read as today's behaviour: paid, counts for holidays.
        setCatIsUnpaid(c.isUnpaid === true);
        setCatCountsHoliday(c.countsForHolidayEligibility !== false);
        setCatModalOpen(true);
    };

    const saveCategory = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!catName.trim()) {
            createPopup("Validation", "Name is required.");
            return;
        }
        const sortNum = parseInt(catSortOrder, 10);
        if (Number.isNaN(sortNum)) {
            createPopup("Validation", "Sort order must be a number.");
            return;
        }
        setLoading(true);
        try {
            if (editingCategory) {
                await TimeOffAPI.patchTimeOffCategory(editingCategory.timeOffCategoryID, {
                    name: catName.trim(),
                    description: catDescription.trim() || null,
                    sortOrder: sortNum,
                    // Unchanged flags are a no-op server-side; a CHANGED isUnpaid can come
                    // back 409 (requests exist, or policy rows/balances contradict it) and
                    // its message is shown as-is below.
                    isUnpaid: catIsUnpaid,
                    countsForHolidayEligibility: catCountsHoliday,
                });
                showToast("Category updated");
            } else {
                await TimeOffAPI.createTimeOffCategory({
                    name: catName.trim(),
                    description: catDescription.trim() || null,
                    sortOrder: sortNum,
                    isUnpaid: catIsUnpaid,
                    countsForHolidayEligibility: catCountsHoliday,
                });
                showToast("Category created");
            }
            setCatModalOpen(false);
            await loadCategories();
        } catch (err) {
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const toggleCategoryActive = async (c: TimeOffCategory) => {
        setLoading(true);
        try {
            if (c.active) {
                await TimeOffAPI.deactivateTimeOffCategory(c.timeOffCategoryID);
                showToast("Category deactivated");
            } else {
                await TimeOffAPI.activateTimeOffCategory(c.timeOffCategoryID);
                showToast("Category reactivated");
            }
            await loadCategories();
        } catch (err) {
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    // ─── Policy handlers ──────────────────────────────────────────────────────
    const openCreatePolicyModal = () => {
        setPolicyModalMode("create");
        setPName("");
        setPDescription("");
        setPSeniority("HIRE_DATE");
        setPTierTiming("NEXT_PERIOD");
        setPLumpMonth("");
        setPLumpDay("");
        setPBalance("INTERNAL");
        setPHolidayPolicyId("");
        setPHolidayEligibilityDays("");
        setPolicyModalOpen(true);
    };

    const openEditPolicyModal = () => {
        if (!policyDetail) return;
        setPolicyModalMode("edit");
        setPName(policyDetail.name);
        setPDescription(policyDetail.description ?? "");
        setPSeniority(policyDetail.seniorityDateSource);
        setPTierTiming(policyDetail.tierAdvancementTiming);
        setPLumpMonth(policyDetail.lumpSumMonth != null ? String(policyDetail.lumpSumMonth) : "");
        setPLumpDay(policyDetail.lumpSumDay != null ? String(policyDetail.lumpSumDay) : "");
        setPBalance(policyDetail.balanceSource);
        setPHolidayPolicyId(String(policyDetail.holidayPolicyID));
        setPHolidayEligibilityDays(
            policyDetail.holidayEligibilityDays != null ? String(policyDetail.holidayEligibilityDays) : ""
        );
        setPolicyModalOpen(true);
    };

    const buildPolicyBody = (): CreateTimeOffPolicyBody | null => {
        if (!pName.trim()) {
            createPopup("Validation", "Name is required.");
            return null;
        }
        const hpId = parseInt(pHolidayPolicyId, 10);
        if (Number.isNaN(hpId)) {
            createPopup("Validation", "Holiday Policy is required.");
            return null;
        }
        let lumpM: number | null = null;
        let lumpD: number | null = null;
        const hasM = pLumpMonth.trim() !== "";
        const hasD = pLumpDay.trim() !== "";
        if (hasM !== hasD) {
            createPopup("Validation", "Set both lump sum month and day, or leave both empty.");
            return null;
        }
        if (hasM && hasD) {
            lumpM = parseInt(pLumpMonth, 10);
            lumpD = parseInt(pLumpDay, 10);
            if (Number.isNaN(lumpM) || Number.isNaN(lumpD)) {
                createPopup("Validation", "Lump sum month and day must be numbers.");
                return null;
            }
        }
        const eligDays = pHolidayEligibilityDays.trim() !== "" ? parseInt(pHolidayEligibilityDays, 10) : null;
        return {
            name: pName.trim(),
            description: pDescription.trim() || null,
            seniorityDateSource: pSeniority,
            tierAdvancementTiming: pTierTiming,
            lumpSumMonth: lumpM,
            lumpSumDay: lumpD,
            balanceSource: pBalance,
            holidayPolicyID: hpId,
            holidayEligibilityDays: eligDays,
        };
    };

    const savePolicy = async (e: React.FormEvent) => {
        e.preventDefault();
        const body = buildPolicyBody();
        if (!body) return;
        setLoading(true);
        try {
            if (policyModalMode === "create") {
                const created = await TimeOffAPI.createTimeOffPolicy(body);
                showToast("Policy created");
                setPolicyModalOpen(false);
                setSelectedPolicyId(created.timeOffPolicyID);
                setPolicyDetail(created);
                await loadPolicies();
            } else if (selectedPolicyId != null) {
                const patch: PatchTimeOffPolicyBody = {
                    name: body.name,
                    description: body.description,
                    seniorityDateSource: body.seniorityDateSource,
                    tierAdvancementTiming: body.tierAdvancementTiming,
                    lumpSumMonth: body.lumpSumMonth,
                    lumpSumDay: body.lumpSumDay,
                    balanceSource: body.balanceSource,
                    holidayPolicyID: body.holidayPolicyID,
                    holidayEligibilityDays: body.holidayEligibilityDays,
                };
                await TimeOffAPI.patchTimeOffPolicy(selectedPolicyId, patch);
                showToast("Policy updated");
                setPolicyModalOpen(false);
                await loadPolicies();
                await loadPolicyDetail(selectedPolicyId);
            }
        } catch (err) {
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const togglePolicyActive = async (p: TimeOffPolicySummary) => {
        setLoading(true);
        try {
            if (p.active) {
                await TimeOffAPI.deactivateTimeOffPolicy(p.timeOffPolicyID);
                showToast("Policy deactivated");
            } else {
                await TimeOffAPI.activateTimeOffPolicy(p.timeOffPolicyID);
                showToast("Policy reactivated");
            }
            await loadPolicies();
            if (selectedPolicyId === p.timeOffPolicyID) {
                await loadPolicyDetail(p.timeOffPolicyID);
            }
        } catch (err) {
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const toggleCat = (pcId: number) => {
        setOpenCats((prev) => {
            const next = new Set(prev);
            if (next.has(pcId)) next.delete(pcId);
            else next.add(pcId);
            return next;
        });
    };

    // ─── Policy category (add/edit) handlers ──────────────────────────────────
    const openAddPolicyCategory = () => {
        setPcModalMode("add");
        setEditingPc(null);
        setPcCatId("");
        setPcCashOut(false);
        setPcCashMax("");
        setPcAnnualMax("");
        setPcEligDays("");
        setPcSort("0");
        setPcRequiresPaidExhausted(false);
        setPcExhaustedBelow("");
        setPcModalOpen(true);
    };

    const openEditPolicyCategory = (pc: TimeOffPolicyCategoryDetail) => {
        setPcModalMode("edit");
        setEditingPc(pc);
        setPcCatId(String(pc.timeOffCategoryID));
        setPcCashOut(pc.cashOutAllowed);
        setPcCashMax(pc.cashOutMaxHours != null ? String(pc.cashOutMaxHours) : "");
        setPcAnnualMax(pc.maxAnnualUsageHours != null ? String(pc.maxAnnualUsageHours) : "");
        setPcEligDays(pc.eligibilityDays ? String(pc.eligibilityDays) : "");
        setPcSort(String(pc.sortOrder ?? 0));
        setPcRequiresPaidExhausted(pc.requiresPaidExhausted === true);
        setPcExhaustedBelow(pc.exhaustedBelowHours != null ? String(pc.exhaustedBelowHours) : "");
        setPcModalOpen(true);
    };

    /** Whether the row being added/edited is an Unpaid Time category (TAS-586). Edit reads the
     *  row's own flag; add looks the picked category up. Missing flags read as paid. */
    const pcIsUnpaid =
        pcModalMode === "edit"
            ? editingPc?.isUnpaid === true
            : (categoryOptions ?? []).some((c) => String(c.timeOffCategoryID) === pcCatId && c.isUnpaid === true);

    const savePolicyCategory = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedPolicyId) return;
        const unpaid = pcIsUnpaid;

        // annual max: blank → null; else parseFloat, must be > 0
        let annualMax: number | null = null;
        if (!unpaid && pcAnnualMax.trim() !== "") {
            annualMax = parseFloat(pcAnnualMax);
            if (Number.isNaN(annualMax) || annualMax <= 0) {
                createPopup("Validation", "Max hours per calendar year must be a positive number (or blank for unlimited).");
                return;
            }
        }
        // cash-out max: blank → null; else numeric
        let cashMax: number | null = null;
        if (!unpaid && pcCashMax.trim() !== "") {
            cashMax = parseFloat(pcCashMax);
            if (Number.isNaN(cashMax)) {
                createPopup("Validation", "Cash-out max hours must be a number.");
                return;
            }
        }
        // eligibility days: blank → 0; else parseInt, must be ≥ 0
        let eligDays = 0;
        if (pcEligDays.trim() !== "") {
            eligDays = parseInt(pcEligDays, 10);
            if (Number.isNaN(eligDays) || eligDays < 0) {
                createPopup("Validation", "Eligibility days must be zero or a positive whole number.");
                return;
            }
        }
        const sortNum = parseInt(pcSort, 10);
        if (Number.isNaN(sortNum)) {
            createPopup("Validation", "Sort order must be a number.");
            return;
        }
        // "counts as used up below" (paid rows): blank → null = this category doesn't gate unpaid time
        let exhaustedBelow: number | null = null;
        if (!unpaid && pcExhaustedBelow.trim() !== "") {
            exhaustedBelow = parseFloat(pcExhaustedBelow);
            if (Number.isNaN(exhaustedBelow) || exhaustedBelow <= 0) {
                createPopup("Validation", "\"Counts as used up below\" must be a positive number of hours (or blank).");
                return;
            }
        }
        // Unpaid rows carry no cap or cash-out and only the "paid first" switch; paid rows
        // only the threshold. Each side sends just its own field — the API refuses the other.
        const unpaidFields: Pick<PatchPolicyCategoryBody, "requiresPaidExhausted" | "exhaustedBelowHours"> = unpaid
            ? { requiresPaidExhausted: pcRequiresPaidExhausted }
            : { exhaustedBelowHours: exhaustedBelow };
        const cashOutAllowed = unpaid ? false : pcCashOut;

        setLoading(true);
        try {
            if (pcModalMode === "edit" && editingPc) {
                const patch: PatchPolicyCategoryBody = {
                    cashOutAllowed,
                    cashOutMaxHours: cashMax,
                    maxAnnualUsageHours: annualMax,
                    eligibilityDays: eligDays,
                    sortOrder: sortNum,
                    ...unpaidFields,
                };
                await TimeOffAPI.patchPolicyCategory(selectedPolicyId, editingPc.timeOffPolicyCategoryID, patch);
                showToast("Settings saved");
            } else {
                const cid = parseInt(pcCatId, 10);
                if (Number.isNaN(cid)) {
                    createPopup("Validation", "Select a category.");
                    setLoading(false);
                    return;
                }
                const body: AddPolicyCategoryBody = {
                    timeOffCategoryID: cid,
                    cashOutAllowed,
                    cashOutMaxHours: cashMax,
                    maxAnnualUsageHours: annualMax,
                    eligibilityDays: eligDays,
                    sortOrder: sortNum,
                    ...unpaidFields,
                };
                await TimeOffAPI.addPolicyCategory(selectedPolicyId, body);
                showToast("Category added to policy");
            }
            setPcModalOpen(false);
            await loadPolicyDetail(selectedPolicyId);
            await loadPolicies();
        } catch (err) {
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const doRemoveCategory = async (pc: TimeOffPolicyCategoryDetail) => {
        if (!selectedPolicyId) return;
        setConfirmState(null);
        setLoading(true);
        try {
            await TimeOffAPI.deactivatePolicyCategory(selectedPolicyId, pc.timeOffPolicyCategoryID);
            showToast("Category removed from policy");
            await loadPolicyDetail(selectedPolicyId);
        } catch (err) {
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const removeCategoryFromPolicy = (pc: TimeOffPolicyCategoryDetail) => {
        setConfirmState({
            variant: "warn",
            icon: <Ban size={22} />,
            title: `Remove “${pc.categoryName}” from this policy?`,
            sub: "The category is deactivated on this policy, not deleted. Employee balances are retained.",
            confirmIcon: <Ban size={16} />,
            confirmLabel: "Remove category",
            onConfirm: () => doRemoveCategory(pc),
        });
    };

    // ─── Tier handlers ────────────────────────────────────────────────────────
    const openAddTier = (pcId: number) => {
        setTierModalPcId(pcId);
        setEditingTier(null);
        setTMinYrs("0");
        setTAccrual("PER_PERIOD");
        setTPerPeriod("");
        setTLumpSumH("");
        setTCap("");
        setTRollover(true);
        setTSort("0");
        setTierModalOpen(true);
    };

    const openEditTier = (pcId: number, tier: TimeOffPolicyTier) => {
        setTierModalPcId(pcId);
        setEditingTier(tier);
        setTMinYrs(String(tier.minYearsOfService));
        setTAccrual(tier.accrualMethod);
        setTPerPeriod(tier.perPeriodHours != null ? String(tier.perPeriodHours) : "");
        setTLumpSumH(tier.lumpSumHours != null ? String(tier.lumpSumHours) : "");
        setTCap(String(tier.maxBalanceCapHours));
        setTRollover(tier.rolloverAllowed);
        setTSort(String(tier.sortOrder ?? 0));
        setTierModalOpen(true);
    };

    const saveTier = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedPolicyId || tierModalPcId == null) return;
        const minY = parseFloat(tMinYrs);
        if (Number.isNaN(minY)) {
            createPopup("Validation", "Min years of service must be a number.");
            return;
        }
        const cap = parseFloat(tCap);
        if (Number.isNaN(cap)) {
            createPopup("Validation", "Max balance cap hours is required.");
            return;
        }
        const sortNum = parseInt(tSort, 10);
        if (Number.isNaN(sortNum)) {
            createPopup("Validation", "Sort order must be a number.");
            return;
        }
        let perP: number | null = null;
        if (tPerPeriod.trim() !== "") {
            perP = parseFloat(tPerPeriod);
            if (Number.isNaN(perP)) {
                createPopup("Validation", "Per period hours must be a number.");
                return;
            }
        }
        let lumpH: number | null = null;
        if (tLumpSumH.trim() !== "") {
            lumpH = parseFloat(tLumpSumH);
            if (Number.isNaN(lumpH)) {
                createPopup("Validation", "Lump sum hours must be a number.");
                return;
            }
        }

        const base: AddPolicyTierBody = {
            minYearsOfService: minY,
            accrualMethod: tAccrual,
            perPeriodHours: tAccrual === "PER_PERIOD" || tAccrual === "LUMP_SUM_AND_PER_PERIOD" ? perP : null,
            lumpSumHours: tAccrual === "LUMP_SUM" || tAccrual === "LUMP_SUM_AND_PER_PERIOD" ? lumpH : null,
            maxBalanceCapHours: cap,
            rolloverAllowed: tRollover,
            sortOrder: sortNum,
        };

        setLoading(true);
        try {
            if (editingTier) {
                await TimeOffAPI.patchPolicyTier(selectedPolicyId, tierModalPcId, editingTier.timeOffPolicyTierID, base);
                showToast("Tier updated");
            } else {
                await TimeOffAPI.addPolicyTier(selectedPolicyId, tierModalPcId, base);
                showToast("Tier added");
            }
            setTierModalOpen(false);
            await loadPolicyDetail(selectedPolicyId);
        } catch (err) {
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const doDeleteTier = async (pcId: number, tier: TimeOffPolicyTier) => {
        if (!selectedPolicyId) return;
        setConfirmState(null);
        setLoading(true);
        try {
            await TimeOffAPI.deletePolicyTier(selectedPolicyId, pcId, tier.timeOffPolicyTierID);
            showToast("Tier deleted");
            await loadPolicyDetail(selectedPolicyId);
        } catch (err) {
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const deleteTier = (pc: TimeOffPolicyCategoryDetail, tier: TimeOffPolicyTier) => {
        setConfirmState({
            variant: "danger",
            icon: <Trash2 size={22} />,
            title: "Delete this tier?",
            sub: "Tiers are permanently removed — this cannot be undone.",
            body: (
                <div className="reqline">
                    <div>
                        <div className="nm">
                            {pc.categoryName} · YOS ≥ {tier.minYearsOfService}
                        </div>
                        <div className="meta">
                            {LABELS.accrualMethod[tier.accrualMethod]} · cap {hoursLabel(tier.maxBalanceCapHours)}
                        </div>
                    </div>
                </div>
            ),
            confirmIcon: <Trash2 size={16} />,
            confirmLabel: "Delete tier",
            onConfirm: () => doDeleteTier(pc.timeOffPolicyCategoryID, tier),
        });
    };

    // ─── Holiday calendar handlers ────────────────────────────────────────────
    const openCreateHc = () => {
        setEditingHc(null);
        setHcDate("");
        setHcName("");
        setHcPayCodeID("");
        setHcPaidHours("");
        setHcRequireBefore(false);
        setHcRequireAfter(false);
        setHcRequireBeforeDate("");
        setHcRequireAfterDate("");
        setHcStack(false);
        setHcModalOpen(true);
    };

    const openEditHc = (entry: HolidayCalendarEntry) => {
        setEditingHc(entry);
        setHcDate(entry.holidayDate.slice(0, 10));
        setHcName(entry.name);
        setHcPayCodeID(String(entry.holidayPayCodeID));
        setHcPaidHours(String(entry.paidHoursIfNotWorked / 60));
        setHcRequireBefore(entry.requireWorkDayBefore);
        setHcRequireAfter(entry.requireWorkDayAfter);
        setHcRequireBeforeDate(entry.requiredWorkDayBeforeDate ? entry.requiredWorkDayBeforeDate.slice(0, 10) : "");
        setHcRequireAfterDate(entry.requiredWorkDayAfterDate ? entry.requiredWorkDayAfterDate.slice(0, 10) : "");
        setHcStack(entry.stackWithWorkedHours);
        setHcModalOpen(true);
    };

    const saveHc = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!hcName.trim()) {
            createPopup("Validation", "Name is required.");
            return;
        }
        const payCodeId = parseInt(hcPayCodeID, 10);
        if (Number.isNaN(payCodeId)) {
            createPopup("Validation", "Pay code ID must be a number.");
            return;
        }
        const hoursVal = parseFloat(hcPaidHours);
        if (Number.isNaN(hoursVal)) {
            createPopup("Validation", "Paid hours must be a number.");
            return;
        }
        const minutes = Math.round(hoursVal * 60);
        setLoading(true);
        try {
            if (editingHc) {
                await TimeOffAPI.patchHolidayCalendar(editingHc.holidayCalendarID, {
                    name: hcName.trim(),
                    holidayPayCodeID: payCodeId,
                    paidHoursIfNotWorked: minutes,
                    requireWorkDayBefore: hcRequireBefore,
                    requireWorkDayAfter: hcRequireAfter,
                    requiredWorkDayBeforeDate: hcRequireBefore ? hcRequireBeforeDate || null : null,
                    requiredWorkDayAfterDate: hcRequireAfter ? hcRequireAfterDate || null : null,
                    stackWithWorkedHours: hcStack,
                });
                showToast("Holiday updated");
            } else {
                if (!hcDate) {
                    createPopup("Validation", "Date is required.");
                    setLoading(false);
                    return;
                }
                await TimeOffAPI.createHolidayCalendar({
                    holidayDate: hcDate,
                    name: hcName.trim(),
                    holidayPayCodeID: payCodeId,
                    paidHoursIfNotWorked: minutes,
                    requireWorkDayBefore: hcRequireBefore,
                    requireWorkDayAfter: hcRequireAfter,
                    requiredWorkDayBeforeDate: hcRequireBefore ? hcRequireBeforeDate || null : null,
                    requiredWorkDayAfterDate: hcRequireAfter ? hcRequireAfterDate || null : null,
                    stackWithWorkedHours: hcStack,
                });
                showToast("Holiday created");
            }
            setHcModalOpen(false);
            await loadHolidayCalendar();
        } catch (err) {
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const toggleHcActive = async (entry: HolidayCalendarEntry) => {
        setLoading(true);
        try {
            if (entry.active) {
                await TimeOffAPI.deactivateHolidayCalendar(entry.holidayCalendarID);
                showToast("Holiday deactivated");
            } else {
                await TimeOffAPI.activateHolidayCalendar(entry.holidayCalendarID);
                showToast("Holiday reactivated");
            }
            await loadHolidayCalendar();
        } catch (err) {
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    // ─── Holiday policy handlers ──────────────────────────────────────────────
    const openCreateHp = () => {
        setEditingHp(null);
        setHpName("");
        setHpDesc("");
        setHpModalOpen(true);
    };

    const openEditHp = () => {
        if (!hpDetail) return;
        setEditingHp(hpDetail);
        setHpName(hpDetail.name);
        setHpDesc(hpDetail.description ?? "");
        setHpModalOpen(true);
    };

    const saveHp = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!hpName.trim()) {
            createPopup("Validation", "Name is required.");
            return;
        }
        setLoading(true);
        try {
            if (editingHp) {
                await TimeOffAPI.patchHolidayPolicy(editingHp.holidayPolicyID, {
                    name: hpName.trim(),
                    description: hpDesc.trim() || null,
                });
                showToast("Holiday policy updated");
                setHpModalOpen(false);
                await loadHolidayPolicies();
                if (selectedHpId != null) await loadHpDetail(selectedHpId);
            } else {
                const created = await TimeOffAPI.createHolidayPolicy({
                    name: hpName.trim(),
                    description: hpDesc.trim() || null,
                });
                showToast("Holiday policy created");
                setHpModalOpen(false);
                await loadHolidayPolicies();
                setSelectedHpId(created.holidayPolicyID);
            }
        } catch (err) {
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const toggleHpActive = async () => {
        if (!hpDetail) return;
        setLoading(true);
        try {
            if (hpDetail.active) {
                await TimeOffAPI.deactivateHolidayPolicy(hpDetail.holidayPolicyID);
                showToast("Holiday policy deactivated");
            } else {
                await TimeOffAPI.activateHolidayPolicy(hpDetail.holidayPolicyID);
                showToast("Holiday policy reactivated");
            }
            await loadHolidayPolicies();
            await loadHpDetail(hpDetail.holidayPolicyID);
        } catch (err) {
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const openAddHpHolModal = async () => {
        try {
            setLoading(true);
            const all = await TimeOffAPI.getHolidayCalendar(false);
            setAllHcForPicker(all);
            setPickerSelected(new Set());
            setAddHpHolModalOpen(true);
        } catch (err) {
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const addSelectedHolidays = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedHpId || pickerSelected.size === 0) {
            setAddHpHolModalOpen(false);
            return;
        }
        setLoading(true);
        try {
            for (const hcId of Array.from(pickerSelected)) {
                await TimeOffAPI.addHolidayToPolicy(selectedHpId, hcId);
            }
            showToast(pickerSelected.size === 1 ? "Holiday added" : "Holidays added");
            setAddHpHolModalOpen(false);
            await loadHpDetail(selectedHpId);
        } catch (err) {
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const doRemoveHoliday = async (hcId: number) => {
        if (!selectedHpId) return;
        setConfirmState(null);
        setLoading(true);
        try {
            await TimeOffAPI.removeHolidayFromPolicy(selectedHpId, hcId);
            showToast("Holiday removed");
            await loadHpDetail(selectedHpId);
        } catch (err) {
            createPopup("Error", TimeOffAPI.getTimeOffErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const removeHolidayFromPolicy = (h: HolidayCalendarEntry) => {
        setConfirmState({
            variant: "warn",
            icon: <X size={22} />,
            title: `Remove “${h.name}”?`,
            sub: "Removes it from this holiday policy only; the calendar entry itself is kept.",
            confirmIcon: <X size={16} />,
            confirmLabel: "Remove",
            onConfirm: () => doRemoveHoliday(h.holidayCalendarID),
        });
    };

    // ─── Derived / filtered data ──────────────────────────────────────────────
    const holidayPolicyName = (id: number): string =>
        (hpList ?? []).find((hp) => hp.holidayPolicyID === id)?.name ?? `#${id}`;

    const activeCategoriesForSelect = (categoryOptions ?? []).filter((c) => c.active);

    const cs = catSearch.trim().toLowerCase();
    const filteredCategories = (categories ?? []).filter((c) => !cs || c.name.toLowerCase().includes(cs));

    const ps = polSearch.trim().toLowerCase();
    const filteredPolicies = (policies ?? []).filter(
        (p) => !ps || p.name.toLowerCase().includes(ps) || (p.description ?? "").toLowerCase().includes(ps)
    );

    const hs = hcSearch.trim().toLowerCase();
    const sortedHc = [...(hcEntries ?? [])].sort((a, b) => a.holidayDate.localeCompare(b.holidayDate));
    const filteredHc = sortedHc.filter((h) => !hs || h.name.toLowerCase().includes(hs));

    const hps = hpSearch.trim().toLowerCase();
    const filteredHp = (hpList ?? []).filter((p) => !hps || p.name.toLowerCase().includes(hps));

    // ─── Access gate ──────────────────────────────────────────────────────────
    if (!config.showTimeOffConfig || !userRole.admin) {
        return (
            <PageWrapper title={translator.translate("timeOffConfiguration")} backgroundColor="#f4f5f7">
                <div className="toc">
                    <div style={{ padding: 24 }}>Access denied.</div>
                </div>
            </PageWrapper>
        );
    }

    // ─── Render helpers ───────────────────────────────────────────────────────
    const renderReqCell = (flag: boolean, date: string | null) => {
        if (!flag) return <span className="dim">—</span>;
        return date ? (
            <span className="toc-mono">{fmtDate(date)}</span>
        ) : (
            <span className="toc-tag">
                <Check size={12} /> Adjacent day
            </span>
        );
    };

    const renderCategoryBlock = (pc: TimeOffPolicyCategoryDetail) => {
        const open = openCats.has(pc.timeOffPolicyCategoryID);
        const colorClass = categoryColorClass(pc.categoryName, pc.timeOffCategoryID);
        const eligDays = pc.eligibilityDays ?? 0;

        // balance cap summary across tiers
        const caps = pc.tiers.map((t) => t.maxBalanceCapHours);
        const capMuted = caps.length === 0;
        const capText = capMuted
            ? "Off"
            : Math.min(...caps) === Math.max(...caps)
            ? hoursLabel(caps[0])
            : `${hoursLabel(Math.min(...caps))}–${hoursLabel(Math.max(...caps))}`;

        const annualUnlimited = pc.maxAnnualUsageHours == null;
        const cashOff = !pc.cashOutAllowed;
        const cashValue = pc.cashOutAllowed
            ? pc.cashOutMaxHours == null
                ? "Allowed"
                : `≤ ${hoursLabel(pc.cashOutMaxHours)}`
            : "Off";

        const sortedTiers = [...pc.tiers].sort((a, b) => a.minYearsOfService - b.minYearsOfService);
        // Unpaid Time rows have no balance, cap, cash-out or tiers — the API refuses all four.
        const unpaid = pc.isUnpaid === true;

        return (
            <div
                key={pc.timeOffPolicyCategoryID}
                className={`toc-catblock ${colorClass}${open ? " open" : ""}${pc.active ? "" : " is-inactive"}`}
            >
                <div className="cb-head" onClick={() => toggleCat(pc.timeOffPolicyCategoryID)}>
                    <span className="cb-chev">
                        <ChevronDown size={16} />
                    </span>
                    <span className="cb-name">{pc.categoryName}</span>
                    {!pc.active && <StatusPill active={false} />}
                    {unpaid ? (
                        <span className="cb-tiercount">· Unpaid</span>
                    ) : (
                        <span className="cb-tiercount">
                            · {pc.tiers.length} tier{pc.tiers.length === 1 ? "" : "s"}
                        </span>
                    )}
                    {unpaid ? (
                        <span className="toc-hstats">
                            <HStat
                                variant="annual"
                                icon={<Gauge size={14} />}
                                label="Paid time first"
                                value={pc.requiresPaidExhausted ? "Required" : "Not required"}
                                muted={!pc.requiresPaidExhausted}
                            />
                        </span>
                    ) : (
                    <span className="toc-hstats">
                        <HStat
                            variant="annual"
                            icon={<CalendarClock size={14} />}
                            label="Annual max"
                            value={annualUnlimited ? "Unlimited" : hoursLabel(pc.maxAnnualUsageHours)}
                            muted={annualUnlimited}
                        />
                        <HStat
                            variant="cashout"
                            icon={<Coins size={14} />}
                            label="Cash-out"
                            value={cashValue}
                            muted={cashOff}
                        />
                        <HStat variant="cap" icon={<Gauge size={14} />} label="Balance cap" value={capText} muted={capMuted} />
                    </span>
                    )}
                </div>

                {open && (
                    <div className="cb-body">
                        <div className="toc-cb-actions">
                            <span className="toc-tag">
                                <UserCheck size={12} />
                                {eligDays > 0 ? `Eligible after ${eligDays} days of employment` : "Eligible from day 1"}
                            </span>
                            {!unpaid && pc.exhaustedBelowHours != null && (
                                <span className="toc-tag" title="Unpaid time that requires paid time used up first checks this threshold">
                                    Used up below {hoursLabel(pc.exhaustedBelowHours)}
                                </span>
                            )}
                            <span className="grow" />
                            <button type="button" className="toc-abtn" onClick={() => openEditPolicyCategory(pc)}>
                                <Sliders size={14} /> Edit settings
                            </button>
                            {!unpaid && (
                                <button type="button" className="toc-abtn" onClick={() => openAddTier(pc.timeOffPolicyCategoryID)}>
                                    <Plus size={14} /> Add tier
                                </button>
                            )}
                            {pc.active && (
                                <button type="button" className="toc-abtn danger" onClick={() => removeCategoryFromPolicy(pc)}>
                                    <Ban size={14} /> Remove
                                </button>
                            )}
                        </div>

                        {unpaid && pc.tiers.length === 0 ? (
                            <div className="toc-mini-empty">
                                <Layers size={28} />
                                <div className="et">Unpaid time — no balance</div>
                                <div>Full 8-hour days only. Nothing accrues, nothing is paid or exported.</div>
                            </div>
                        ) : pc.tiers.length > 0 ? (
                            <table className="toc-tier-table">
                                <thead>
                                    <tr>
                                        <th>Seniority</th>
                                        <th>Accrual method</th>
                                        <th className="num">Per period</th>
                                        <th className="num">Lump sum</th>
                                        <th className="num">Balance cap</th>
                                        <th>Rollover</th>
                                        <th className="acts" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedTiers.map((tier) => (
                                        <tr key={tier.timeOffPolicyTierID}>
                                            <td className="yos">
                                                YOS ≥ <span className="toc-mono">{tier.minYearsOfService}</span>
                                            </td>
                                            <td>{LABELS.accrualMethod[tier.accrualMethod]}</td>
                                            <td className="num">
                                                {tier.perPeriodHours != null ? (
                                                    hoursLabel(tier.perPeriodHours)
                                                ) : (
                                                    <span className="dash">—</span>
                                                )}
                                            </td>
                                            <td className="num">
                                                {tier.lumpSumHours != null ? (
                                                    hoursLabel(tier.lumpSumHours)
                                                ) : (
                                                    <span className="dash">—</span>
                                                )}
                                            </td>
                                            <td className="num">{hoursLabel(tier.maxBalanceCapHours)}</td>
                                            <td>
                                                <span className={`rollpill ${tier.rolloverAllowed ? "yes" : "no"}`}>
                                                    {tier.rolloverAllowed ? "Yes" : "No"}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="miniacts">
                                                    <button
                                                        type="button"
                                                        className="toc-abtn icon"
                                                        title="Edit tier"
                                                        onClick={() => openEditTier(pc.timeOffPolicyCategoryID, tier)}
                                                    >
                                                        <Pencil size={15} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="toc-abtn icon danger"
                                                        title="Delete tier"
                                                        onClick={() => deleteTier(pc, tier)}
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="toc-mini-empty">
                                <Layers size={28} />
                                <div className="et">No accrual tiers yet</div>
                                <div>Add a tier to set how this category accrues.</div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // ─── Main render ──────────────────────────────────────────────────────────
    return (
        <PageWrapper title={translator.translate("timeOffConfiguration")} backgroundColor="#f4f5f7">
            <div className="toc">
                {/* breadcrumb */}
                <div className="toc-crumbs">
                    <span>Setup</span>
                    <ChevronRight />
                    <span className="here">Time Off Configuration</span>
                </div>

                {/* header */}
                <div className="toc-app-head">
                    <div className="toc-logo">
                        <Settings size={22} />
                    </div>
                    <div>
                        <h1 className="toc-app-title">Time Off Configuration</h1>
                        <div className="toc-app-sub">
                            Categories, accrual policies, the holiday calendar, and holiday policies.
                        </div>
                    </div>
                </div>

                {/* tabs */}
                <div className="toc-tabs">
                    <button type="button" className={`toc-tab${tab === "categories" ? " on" : ""}`} onClick={() => setTab("categories")}>
                        <Layers size={17} /> Categories
                    </button>
                    <button type="button" className={`toc-tab${tab === "policies" ? " on" : ""}`} onClick={() => setTab("policies")}>
                        <Sliders size={17} /> Policies
                    </button>
                    <button
                        type="button"
                        className={`toc-tab${tab === "holidayCalendar" ? " on" : ""}`}
                        onClick={() => setTab("holidayCalendar")}
                    >
                        <Calendar size={17} /> Holiday Calendar
                    </button>
                    <button
                        type="button"
                        className={`toc-tab${tab === "holidayPolicies" ? " on" : ""}`}
                        onClick={() => setTab("holidayPolicies")}
                    >
                        <CalendarCheck size={17} /> Holiday Policies
                    </button>
                </div>

                {/* ── Categories tab ─────────────────────────────────────────── */}
                {tab === "categories" && (
                    <>
                        <div className="toc-tab-toolbar">
                            <div className="toc-searchwrap">
                                <Search size={17} className="toc-search-icon" />
                                <input
                                    className="toc-input"
                                    placeholder="Search categories…"
                                    value={catSearch}
                                    onChange={(e) => setCatSearch(e.target.value)}
                                />
                            </div>
                            <Toggle checked={catActiveOnly} onChange={() => setCatActiveOnly((v) => !v)} />
                            <button type="button" className="toc-btn primary" onClick={openCreateCategory}>
                                <Plus size={16} /> New category
                            </button>
                        </div>
                        <div className="toc-ccard">
                            <table className="toc-ctable">
                                <thead>
                                    <tr>
                                        <th className="num">#</th>
                                        <th>Category</th>
                                        <th>Description</th>
                                        <th>Status</th>
                                        <th className="acts">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCategories.map((c) => (
                                        <tr key={c.timeOffCategoryID} className={c.active ? "" : "is-inactive"}>
                                            <td className="num dim toc-mono">{c.sortOrder}</td>
                                            <td className="primary-cell">
                                                <span className="toc-catcell">
                                                    <CategoryChip name={c.name} id={c.timeOffCategoryID} />
                                                    {c.isUnpaid === true && (
                                                        <span className="toc-tag" title="No balance; never paid or exported">
                                                            Unpaid
                                                        </span>
                                                    )}
                                                    {c.countsForHolidayEligibility === false && (
                                                        <span className="toc-tag" title="A day of this category does not count toward holiday eligibility">
                                                            No holiday credit
                                                        </span>
                                                    )}
                                                </span>
                                            </td>
                                            <td className="desc-cell">{c.description || "—"}</td>
                                            <td>
                                                <StatusPill active={c.active} />
                                            </td>
                                            <td>
                                                <div className="toc-rowacts">
                                                    <button type="button" className="toc-abtn" onClick={() => openEditCategory(c)}>
                                                        <Pencil size={14} /> Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`toc-abtn${c.active ? " danger" : ""}`}
                                                        onClick={() => toggleCategoryActive(c)}
                                                    >
                                                        {c.active ? (
                                                            <>
                                                                <Ban size={14} /> Deactivate
                                                            </>
                                                        ) : (
                                                            <>
                                                                <RotateCcw size={14} /> Reactivate
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {categories && filteredCategories.length === 0 && (
                                        <tr>
                                            <td colSpan={5}>
                                                <div className="toc-mini-empty">
                                                    <Inbox size={28} />
                                                    <div className="et">No categories</div>
                                                    <div>Turn off “Active only” or adjust your search.</div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {/* ── Policies tab (master / detail) ─────────────────────────── */}
                {tab === "policies" && (
                    <>
                        <div className="toc-tab-toolbar">
                            <div className="toc-searchwrap">
                                <Search size={17} className="toc-search-icon" />
                                <input
                                    className="toc-input"
                                    placeholder="Search policies…"
                                    value={polSearch}
                                    onChange={(e) => setPolSearch(e.target.value)}
                                />
                            </div>
                            <Toggle checked={polActiveOnly} onChange={() => setPolActiveOnly((v) => !v)} />
                            <button type="button" className="toc-btn primary" onClick={openCreatePolicyModal}>
                                <Plus size={16} /> New policy
                            </button>
                        </div>

                        <div className="toc-md-layout">
                            {/* left: policy list */}
                            <div className="toc-policy-list">
                                <div className="toc-pl-head">
                                    <span className="t">Policies</span>
                                    <span className="c">{filteredPolicies.length}</span>
                                </div>
                                {filteredPolicies.map((p) => (
                                    <button
                                        key={p.timeOffPolicyID}
                                        type="button"
                                        className={`toc-policy-item${
                                            selectedPolicyId === p.timeOffPolicyID ? " on" : ""
                                        }${p.active ? "" : " is-inactive"}`}
                                        onClick={() => setSelectedPolicyId(p.timeOffPolicyID)}
                                    >
                                        <div className="nm">{p.name}</div>
                                        <div className="meta">
                                            <StatusPill active={p.active} />
                                            <span className="sub">{LABELS.balanceSource[p.balanceSource].split(" ")[0]}</span>
                                        </div>
                                    </button>
                                ))}
                                {policies && filteredPolicies.length === 0 && (
                                    <div className="toc-mini-empty">
                                        <Inbox size={28} />
                                        <div className="et">No policies</div>
                                    </div>
                                )}
                            </div>

                            {/* right: detail */}
                            {policyDetail && selectedPolicyId === policyDetail.timeOffPolicyID ? (
                                <div className="toc-policy-detail">
                                    <div className="toc-pd-head">
                                        <div>
                                            <div className="pd-title">
                                                {policyDetail.name} <StatusPill active={policyDetail.active} />
                                            </div>
                                            {policyDetail.description && <div className="pd-sub">{policyDetail.description}</div>}
                                        </div>
                                        <div className="pd-actions">
                                            <button type="button" className="toc-btn" onClick={openEditPolicyModal}>
                                                <Pencil size={16} /> Edit policy
                                            </button>
                                            <button type="button" className="toc-btn" onClick={() => togglePolicyActive(policyDetail)}>
                                                {policyDetail.active ? <Ban size={16} /> : <RotateCcw size={16} />}
                                                {policyDetail.active ? "Deactivate" : "Reactivate"}
                                            </button>
                                            <button type="button" className="toc-btn primary" onClick={openAddPolicyCategory}>
                                                <Plus size={16} /> Add category
                                            </button>
                                        </div>
                                    </div>

                                    <div className="toc-pd-meta">
                                        <div className="m">
                                            <div className="k">Seniority date</div>
                                            <div className="v">{LABELS.seniorityDateSource[policyDetail.seniorityDateSource]}</div>
                                        </div>
                                        <div className="m">
                                            <div className="k">Tier advancement</div>
                                            <div className="v">{LABELS.tierAdvancementTiming[policyDetail.tierAdvancementTiming]}</div>
                                        </div>
                                        <div className="m">
                                            <div className="k">Lump-sum reset</div>
                                            <div className="v toc-mono">
                                                {policyDetail.lumpSumMonth != null && policyDetail.lumpSumDay != null
                                                    ? `${policyDetail.lumpSumMonth}/${policyDetail.lumpSumDay}`
                                                    : "—"}
                                            </div>
                                        </div>
                                        <div className="m">
                                            <div className="k">Balance source</div>
                                            <div className="v">{LABELS.balanceSource[policyDetail.balanceSource]}</div>
                                        </div>
                                        <div className="m">
                                            <div className="k">Holiday policy</div>
                                            <div className="v">{holidayPolicyName(policyDetail.holidayPolicyID)}</div>
                                        </div>
                                        <div className="m">
                                            <div className="k">Holiday eligibility</div>
                                            <div className="v">
                                                {policyDetail.holidayEligibilityDays
                                                    ? `${policyDetail.holidayEligibilityDays} days`
                                                    : "From day 1"}
                                            </div>
                                        </div>
                                        <div className="m">
                                            <div className="k">Created</div>
                                            <div className="v toc-mono">{fmtDate(String(policyDetail.createdAt))}</div>
                                        </div>
                                    </div>

                                    <div className="toc-pd-body">
                                        <div className="toc-pd-body-head">
                                            <span className="t">Policy categories</span>
                                            <span className="c">{policyDetail.categories.length} attached</span>
                                        </div>
                                        {policyDetail.categories.length > 0 ? (
                                            policyDetail.categories.map(renderCategoryBlock)
                                        ) : (
                                            <div className="toc-mini-empty">
                                                <Layers size={28} />
                                                <div className="et">No categories attached</div>
                                                <div>Add a policy category to define how time accrues.</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="toc-pd-empty">
                                    <Sliders size={40} />
                                    <div className="et">Select a policy</div>
                                    <div>Choose a policy on the left to view and edit its categories, tiers, and limits.</div>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* ── Holiday Calendar tab ───────────────────────────────────── */}
                {tab === "holidayCalendar" && (
                    <>
                        <div className="toc-tab-toolbar">
                            <div className="toc-searchwrap">
                                <Search size={17} className="toc-search-icon" />
                                <input
                                    className="toc-input"
                                    placeholder="Search holidays…"
                                    value={hcSearch}
                                    onChange={(e) => setHcSearch(e.target.value)}
                                />
                            </div>
                            <Toggle checked={hcActiveOnly} onChange={() => setHcActiveOnly((v) => !v)} />
                            <button type="button" className="toc-btn primary" onClick={openCreateHc}>
                                <Plus size={16} /> New holiday
                            </button>
                        </div>
                        <div className="toc-ccard">
                            <table className="toc-ctable">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Name</th>
                                        <th>Pay code</th>
                                        <th className="num">Paid hrs</th>
                                        <th>Req. before</th>
                                        <th>Req. after</th>
                                        <th>Stack</th>
                                        <th>Status</th>
                                        <th className="acts">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredHc.map((h) => (
                                        <tr key={h.holidayCalendarID} className={h.active ? "" : "is-inactive"}>
                                            <td className="toc-mono">{fmtDate(h.holidayDate)}</td>
                                            <td className="primary-cell">{h.name}</td>
                                            <td className="toc-mono dim">{h.holidayPayCodeID}</td>
                                            <td className="num">{hoursLabel(h.paidHoursIfNotWorked / 60)}</td>
                                            <td>{renderReqCell(h.requireWorkDayBefore, h.requiredWorkDayBeforeDate)}</td>
                                            <td>{renderReqCell(h.requireWorkDayAfter, h.requiredWorkDayAfterDate)}</td>
                                            <td>
                                                {h.stackWithWorkedHours ? (
                                                    <span className="toc-tag">
                                                        <Layers size={12} /> Stacks
                                                    </span>
                                                ) : (
                                                    <span className="dim">—</span>
                                                )}
                                            </td>
                                            <td>
                                                <StatusPill active={h.active} />
                                            </td>
                                            <td>
                                                <div className="toc-rowacts">
                                                    <button type="button" className="toc-abtn" onClick={() => openEditHc(h)}>
                                                        <Pencil size={14} /> Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`toc-abtn${h.active ? " danger" : ""}`}
                                                        onClick={() => toggleHcActive(h)}
                                                    >
                                                        {h.active ? (
                                                            <>
                                                                <Ban size={14} /> Deactivate
                                                            </>
                                                        ) : (
                                                            <>
                                                                <RotateCcw size={14} /> Reactivate
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {hcEntries && filteredHc.length === 0 && (
                                        <tr>
                                            <td colSpan={9}>
                                                <div className="toc-mini-empty">
                                                    <Calendar size={28} />
                                                    <div className="et">No holidays</div>
                                                    <div>Turn off “Active only” or adjust your search.</div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {/* ── Holiday Policies tab (two pane) ────────────────────────── */}
                {tab === "holidayPolicies" && (
                    <>
                        <div className="toc-tab-toolbar">
                            <div className="toc-searchwrap">
                                <Search size={17} className="toc-search-icon" />
                                <input
                                    className="toc-input"
                                    placeholder="Search holiday policies…"
                                    value={hpSearch}
                                    onChange={(e) => setHpSearch(e.target.value)}
                                />
                            </div>
                            <Toggle checked={hpActiveOnly} onChange={() => setHpActiveOnly((v) => !v)} />
                            <button type="button" className="toc-btn primary" onClick={openCreateHp}>
                                <Plus size={16} /> New holiday policy
                            </button>
                        </div>

                        <div className="toc-hp-layout">
                            <div className="toc-policy-list">
                                <div className="toc-pl-head">
                                    <span className="t">Holiday policies</span>
                                    <span className="c">{filteredHp.length}</span>
                                </div>
                                {filteredHp.map((hp) => (
                                    <button
                                        key={hp.holidayPolicyID}
                                        type="button"
                                        className={`toc-policy-item${selectedHpId === hp.holidayPolicyID ? " on" : ""}${
                                            hp.active ? "" : " is-inactive"
                                        }`}
                                        onClick={() => setSelectedHpId(hp.holidayPolicyID)}
                                    >
                                        <div className="nm">{hp.name}</div>
                                        <div className="meta">
                                            <StatusPill active={hp.active} />
                                            <span className="toc-tag">
                                                <Calendar size={12} /> {hp.holidays.length} holidays
                                            </span>
                                        </div>
                                    </button>
                                ))}
                                {hpList && filteredHp.length === 0 && (
                                    <div className="toc-mini-empty">
                                        <Inbox size={28} />
                                        <div className="et">No holiday policies</div>
                                    </div>
                                )}
                            </div>

                            {hpDetail && selectedHpId === hpDetail.holidayPolicyID ? (
                                <div className="toc-policy-detail">
                                    <div className="toc-pd-head">
                                        <div>
                                            <div className="pd-title">
                                                {hpDetail.name} <StatusPill active={hpDetail.active} />
                                            </div>
                                            {hpDetail.description && <div className="pd-sub">{hpDetail.description}</div>}
                                        </div>
                                        <div className="pd-actions">
                                            <button type="button" className="toc-btn" onClick={openEditHp}>
                                                <Pencil size={16} /> Edit
                                            </button>
                                            <button type="button" className="toc-btn" onClick={toggleHpActive}>
                                                {hpDetail.active ? <Ban size={16} /> : <RotateCcw size={16} />}
                                                {hpDetail.active ? "Deactivate" : "Reactivate"}
                                            </button>
                                            <button type="button" className="toc-btn primary" onClick={openAddHpHolModal}>
                                                <Plus size={16} /> Add holiday
                                            </button>
                                        </div>
                                    </div>
                                    <div className="toc-pd-body">
                                        <div className="toc-pd-body-head">
                                            <span className="t">Assigned holidays</span>
                                            <span className="c">{hpDetail.holidays.length} in this policy</span>
                                        </div>
                                        {hpDetail.holidays.length > 0 ? (
                                            <div className="toc-ccard">
                                                <table className="toc-ctable">
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Name</th>
                                                            <th className="num">Paid hrs</th>
                                                            <th className="acts" />
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {[...hpDetail.holidays]
                                                            .sort((a, b) => a.holidayDate.localeCompare(b.holidayDate))
                                                            .map((h) => (
                                                                <tr key={h.holidayCalendarID}>
                                                                    <td className="toc-mono">{fmtDate(h.holidayDate)}</td>
                                                                    <td className="primary-cell">{h.name}</td>
                                                                    <td className="num">{hoursLabel(h.paidHoursIfNotWorked / 60)}</td>
                                                                    <td>
                                                                        <div className="toc-rowacts">
                                                                            <button
                                                                                type="button"
                                                                                className="toc-abtn danger"
                                                                                onClick={() => removeHolidayFromPolicy(h)}
                                                                            >
                                                                                <X size={14} /> Remove
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="toc-mini-empty">
                                                <Calendar size={28} />
                                                <div className="et">No holidays assigned</div>
                                                <div>Add a holiday from the calendar to include it here.</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="toc-pd-empty">
                                    <CalendarCheck size={40} />
                                    <div className="et">Select a holiday policy</div>
                                    <div>Choose a policy on the left to view and manage its holidays.</div>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* ── Category modal ─────────────────────────────────────────── */}
                {catModalOpen && (
                    <FormModal
                        icon={<Layers size={21} />}
                        title={editingCategory ? "Edit category" : "New category"}
                        sub="Global building block referenced by policies."
                        onClose={() => setCatModalOpen(false)}
                        onSubmit={saveCategory}
                        saveLabel={editingCategory ? "Save category" : "Create category"}
                    >
                        <FRow label="Name" required>
                            <input className="toc-input" value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="e.g. PTO" />
                        </FRow>
                        <FRow label="Sort order">
                            <UnitInput value={catSortOrder} onChange={setCatSortOrder} unit="#" placeholder="1" />
                        </FRow>
                        <FRow label="Description" optional="optional" full>
                            <textarea
                                value={catDescription}
                                onChange={(e) => setCatDescription(e.target.value)}
                                placeholder="What this category is for…"
                            />
                        </FRow>
                        <div className="toc-frow full">
                            <label className="toc-flabel">
                                <input
                                    type="checkbox"
                                    checked={catIsUnpaid}
                                    onChange={(e) => setCatIsUnpaid(e.target.checked)}
                                    style={{ width: 16, height: 16, accentColor: "var(--accent)", marginRight: 2 }}
                                />
                                Unpaid (no balance, never paid)
                            </label>
                            <div className="hint">
                                Requests are tracked and approved as usual, in <b>full 8-hour days only</b>. Nothing is deducted,
                                paid or exported. This can&rsquo;t be changed once requests exist for the category.
                            </div>
                        </div>
                        <div className="toc-frow full">
                            <label className="toc-flabel">
                                <input
                                    type="checkbox"
                                    checked={catCountsHoliday}
                                    onChange={(e) => setCatCountsHoliday(e.target.checked)}
                                    style={{ width: 16, height: 16, accentColor: "var(--accent)", marginRight: 2 }}
                                />
                                Counts for holiday eligibility
                            </label>
                            <div className="hint">
                                When on, a day of this time off next to a holiday counts like a day worked for holiday pay.
                            </div>
                        </div>
                    </FormModal>
                )}

                {/* ── Policy modal ───────────────────────────────────────────── */}
                {policyModalOpen && (
                    <FormModal
                        icon={<Sliders size={21} />}
                        title={policyModalMode === "create" ? "New policy" : "Edit policy"}
                        sub="Accrual rules that categories attach to."
                        wide
                        onClose={() => setPolicyModalOpen(false)}
                        onSubmit={savePolicy}
                        saveLabel={policyModalMode === "create" ? "Create policy" : "Save policy"}
                    >
                        <FRow label="Policy name" required>
                            <input className="toc-input" value={pName} onChange={(e) => setPName(e.target.value)} placeholder="e.g. Standard Full-Time" />
                        </FRow>
                        <FRow label="Balance source">
                            <select className="toc-select" value={pBalance} onChange={(e) => setPBalance(e.target.value as BalanceSource)}>
                                {BALANCE_OPTS.map((o) => (
                                    <option key={o} value={o}>
                                        {LABELS.balanceSource[o]}
                                    </option>
                                ))}
                            </select>
                        </FRow>
                        <FRow label="Description" optional="optional" full>
                            <textarea value={pDescription} onChange={(e) => setPDescription(e.target.value)} placeholder="Who this policy applies to…" />
                        </FRow>

                        <FSection title="Seniority & accrual timing" />
                        <FRow label="Seniority date source">
                            <select className="toc-select" value={pSeniority} onChange={(e) => setPSeniority(e.target.value as SeniorityDateSource)}>
                                {SENIORITY_OPTS.map((o) => (
                                    <option key={o} value={o}>
                                        {LABELS.seniorityDateSource[o]}
                                    </option>
                                ))}
                            </select>
                        </FRow>
                        <FRow label="Tier advancement timing">
                            <select
                                className="toc-select"
                                value={pTierTiming}
                                onChange={(e) => setPTierTiming(e.target.value as TierAdvancementTiming)}
                            >
                                {TIER_TIMING_OPTS.map((o) => (
                                    <option key={o} value={o}>
                                        {LABELS.tierAdvancementTiming[o]}
                                    </option>
                                ))}
                            </select>
                        </FRow>
                        <FRow
                            label="Lump-sum reset month"
                            optional="optional"
                            hint={
                                <>
                                    Set month <b>and</b> day together, or leave both blank.
                                </>
                            }
                        >
                            <UnitInput value={pLumpMonth} onChange={setPLumpMonth} unit="mo" placeholder="1–12" min="1" />
                        </FRow>
                        <FRow label="Lump-sum reset day" optional="optional">
                            <UnitInput value={pLumpDay} onChange={setPLumpDay} unit="day" placeholder="1–31" min="1" />
                        </FRow>

                        <FSection title="Holidays" />
                        <FRow label="Holiday policy" required hint="Which set of company holidays applies.">
                            <FloatingSelect
                                value={pHolidayPolicyId}
                                placeholder="Select…"
                                onChange={setPHolidayPolicyId}
                                options={(hpList ?? [])
                                    .filter((hp) => hp.active || String(hp.holidayPolicyID) === pHolidayPolicyId)
                                    .map((hp) => ({ value: String(hp.holidayPolicyID), label: hp.name }))}
                            />
                        </FRow>
                        <FRow
                            label="Holiday eligibility"
                            optional="optional"
                            hint={
                                <>
                                    Days employed before an employee is eligible for holiday pay. <b>Blank = eligible from day 1.</b>
                                </>
                            }
                        >
                            <UnitInput
                                value={pHolidayEligibilityDays}
                                onChange={setPHolidayEligibilityDays}
                                unit="days"
                                placeholder="blank = day 1"
                                min="0"
                            />
                        </FRow>
                    </FormModal>
                )}

                {/* ── Policy category modal (add/edit) ───────────────────────── */}
                {pcModalOpen && (
                    <FormModal
                        icon={<Sliders size={21} />}
                        title={pcModalMode === "edit" ? "Edit policy category" : "Add policy category"}
                        sub="How this category behaves inside the policy."
                        onClose={() => setPcModalOpen(false)}
                        onSubmit={savePolicyCategory}
                        saveLabel={pcModalMode === "edit" ? "Save settings" : "Add category"}
                    >
                        <FRow
                            label="Category"
                            required
                            full
                            hint={pcModalMode === "edit" ? undefined : "Pick a global category to attach to this policy."}
                        >
                            {pcModalMode === "edit" && editingPc ? (
                                <div className="toc-catbox">
                                    <CategoryChip name={editingPc.categoryName} id={editingPc.timeOffCategoryID} />
                                </div>
                            ) : (
                                <FloatingSelect
                                    value={pcCatId}
                                    placeholder="Select a category…"
                                    onChange={setPcCatId}
                                    options={activeCategoriesForSelect.map((c) => ({
                                        value: String(c.timeOffCategoryID),
                                        label: c.name,
                                        dotClass: categoryColorClass(c.name, c.timeOffCategoryID),
                                    }))}
                                />
                            )}
                        </FRow>

                        {pcIsUnpaid ? (
                            <>
                                <FSection title="Unpaid time" />
                                <div className="toc-frow full">
                                    <label className="toc-flabel">
                                        <input
                                            type="checkbox"
                                            checked={pcRequiresPaidExhausted}
                                            onChange={(e) => setPcRequiresPaidExhausted(e.target.checked)}
                                            style={{ width: 16, height: 16, accentColor: "var(--accent)", marginRight: 2 }}
                                        />
                                        Require paid time used up first
                                    </label>
                                    <div className="hint">
                                        Employees can request unpaid time only once every paid category on this policy with a
                                        &ldquo;Counts as used up below&rdquo; amount is used up. Admins can override when entering or
                                        approving a request.
                                    </div>
                                </div>
                            </>
                        ) : (
                        <>
                        <FSection title="Usage & cash-out limits" />
                        <FRow
                            label="Max hours per calendar year"
                            optional="annual usage cap"
                            full
                            hint={
                                <>
                                    Caps hours of this category an employee can <b>use</b> per calendar year (Jan 1–Dec 31).{" "}
                                    <b>Blank = unlimited.</b> Positive numbers only; decimals allowed (e.g. 37.5).
                                </>
                            }
                        >
                            <UnitInput value={pcAnnualMax} onChange={setPcAnnualMax} unit="h / yr" placeholder="blank = unlimited" step="0.5" min="0" />
                        </FRow>
                        <div className="toc-frow full">
                            <label className="toc-flabel">
                                <input
                                    type="checkbox"
                                    checked={pcCashOut}
                                    onChange={(e) => setPcCashOut(e.target.checked)}
                                    style={{ width: 16, height: 16, accentColor: "var(--accent)", marginRight: 2 }}
                                />
                                Cash-out allowed
                            </label>
                        </div>
                        <FRow label="Cash-out max hours" optional="optional" hint="Max hours cashable per cash-out request.">
                            <UnitInput value={pcCashMax} onChange={setPcCashMax} unit="h" placeholder="optional" step="0.5" min="0" />
                        </FRow>
                        <FRow
                            label="Counts as used up below"
                            optional="optional"
                            hint={
                                <>
                                    For unpaid time that requires paid time used up first: this category counts as used up once
                                    the available balance drops below this. <b>Blank = doesn&rsquo;t gate unpaid time.</b>
                                </>
                            }
                        >
                            <UnitInput value={pcExhaustedBelow} onChange={setPcExhaustedBelow} unit="h" placeholder="blank = no gate" step="0.5" min="0" />
                        </FRow>
                        </>
                        )}
                        <FRow
                            label="Eligibility"
                            optional="per-category"
                            hint={
                                <>
                                    Days employed before the employee may <b>request</b> this category. <b>Blank / 0 = from day 1.</b>
                                </>
                            }
                        >
                            <UnitInput value={pcEligDays} onChange={setPcEligDays} unit="days" placeholder="0" min="0" />
                        </FRow>
                        <FRow label="Sort order">
                            <UnitInput value={pcSort} onChange={setPcSort} unit="#" placeholder="1" />
                        </FRow>
                        {!pcIsUnpaid && (
                        <div className="toc-frow full">
                            <div className="toc-callout amber">
                                <AlertTriangle size={16} />
                                <div>
                                    Lowering a cap does <b>not</b> invalidate requests already approved or pending for the current
                                    year — it only affects new submissions.
                                </div>
                            </div>
                        </div>
                        )}
                    </FormModal>
                )}

                {/* ── Tier modal ─────────────────────────────────────────────── */}
                {tierModalOpen && tierModalPcId != null && (
                    <FormModal
                        icon={<Layers size={21} />}
                        title={editingTier ? "Edit tier" : "Add tier"}
                        sub="Seniority-based accrual step."
                        onClose={() => setTierModalOpen(false)}
                        onSubmit={saveTier}
                        saveLabel={editingTier ? "Save tier" : "Add tier"}
                    >
                        <FRow label="Min years of service" required hint="Applies when the employee has at least this many years of service.">
                            <UnitInput value={tMinYrs} onChange={setTMinYrs} unit="yrs" placeholder="0" min="0" />
                        </FRow>
                        <FRow label="Accrual method" required>
                            <select className="toc-select" value={tAccrual} onChange={(e) => setTAccrual(e.target.value as AccrualMethod)}>
                                {ACCRUAL_OPTS.map((o) => (
                                    <option key={o} value={o}>
                                        {LABELS.accrualMethod[o]}
                                    </option>
                                ))}
                            </select>
                        </FRow>
                        {(tAccrual === "PER_PERIOD" || tAccrual === "LUMP_SUM_AND_PER_PERIOD") && (
                            <FRow label="Per-period hours" hint="Accrued each pay period.">
                                <UnitInput value={tPerPeriod} onChange={setTPerPeriod} unit="h" placeholder="e.g. 3.08" step="0.01" min="0" />
                            </FRow>
                        )}
                        {(tAccrual === "LUMP_SUM" || tAccrual === "LUMP_SUM_AND_PER_PERIOD") && (
                            <FRow label="Lump-sum hours" hint="Granted at the reset date.">
                                <UnitInput value={tLumpSumH} onChange={setTLumpSumH} unit="h" placeholder="e.g. 40" step="0.5" min="0" />
                            </FRow>
                        )}
                        <FRow label="Max balance cap" required hint="Accrual stops once the balance reaches this cap.">
                            <UnitInput value={tCap} onChange={setTCap} unit="h" placeholder="e.g. 160" step="0.5" min="0" />
                        </FRow>
                        <FRow label="Sort order">
                            <UnitInput value={tSort} onChange={setTSort} unit="#" placeholder="1" />
                        </FRow>
                        <div className="toc-frow full">
                            <label className="toc-checkrow">
                                <input type="checkbox" checked={tRollover} onChange={(e) => setTRollover(e.target.checked)} />
                                <span className="cr-tx">
                                    <span className="cr-t">Rollover allowed</span>
                                    <span className="cr-s">Unused balance carries into the next year (up to the balance cap).</span>
                                </span>
                            </label>
                        </div>
                    </FormModal>
                )}

                {/* ── Holiday calendar modal ─────────────────────────────────── */}
                {hcModalOpen && (
                    <FormModal
                        icon={<Calendar size={21} />}
                        title={editingHc ? "Edit holiday" : "New holiday"}
                        sub="A company-wide holiday date entry."
                        wide
                        onClose={() => setHcModalOpen(false)}
                        onSubmit={saveHc}
                        saveLabel={editingHc ? "Save holiday" : "Create holiday"}
                    >
                        <FRow label="Date" required hint={editingHc ? "Date is locked when editing." : undefined}>
                            <input
                                className="toc-input"
                                type="date"
                                value={hcDate}
                                onChange={(e) => setHcDate(e.target.value)}
                                disabled={!!editingHc}
                            />
                        </FRow>
                        <FRow label="Name" required>
                            <input className="toc-input" value={hcName} onChange={(e) => setHcName(e.target.value)} placeholder="e.g. Labor Day" />
                        </FRow>
                        <FRow label="Pay code ID" required>
                            <UnitInput value={hcPayCodeID} onChange={setHcPayCodeID} unit="#" placeholder="e.g. 901" min="0" />
                        </FRow>
                        <FRow
                            label="Paid hours if not worked"
                            required
                            hint={
                                <>
                                    Entered in <b>hours</b>; stored as minutes.
                                </>
                            }
                        >
                            <UnitInput value={hcPaidHours} onChange={setHcPaidHours} unit="h" placeholder="e.g. 8" step="0.25" min="0" />
                        </FRow>

                        <FSection title="Work-day requirements" />
                        <div className="toc-frow full">
                            <label className="toc-checkrow">
                                <input
                                    type="checkbox"
                                    checked={hcRequireBefore}
                                    onChange={(e) => {
                                        setHcRequireBefore(e.target.checked);
                                        if (!e.target.checked) setHcRequireBeforeDate("");
                                    }}
                                />
                                <span className="cr-tx">
                                    <span className="cr-t">Require work day before</span>
                                    <span className="cr-s">
                                        Employee must work the scheduled day before to receive holiday pay. Leave the date blank to use
                                        the adjacent calendar day.
                                    </span>
                                </span>
                            </label>
                        </div>
                        {hcRequireBefore && (
                            <FRow label='Specific "before" date' optional="blank = adjacent day">
                                <input
                                    className="toc-input"
                                    type="date"
                                    value={hcRequireBeforeDate}
                                    onChange={(e) => setHcRequireBeforeDate(e.target.value)}
                                />
                            </FRow>
                        )}
                        <div className="toc-frow full">
                            <label className="toc-checkrow">
                                <input
                                    type="checkbox"
                                    checked={hcRequireAfter}
                                    onChange={(e) => {
                                        setHcRequireAfter(e.target.checked);
                                        if (!e.target.checked) setHcRequireAfterDate("");
                                    }}
                                />
                                <span className="cr-tx">
                                    <span className="cr-t">Require work day after</span>
                                    <span className="cr-s">Employee must work the scheduled day after to receive holiday pay.</span>
                                </span>
                            </label>
                        </div>
                        {hcRequireAfter && (
                            <FRow label='Specific "after" date' optional="blank = adjacent day">
                                <input
                                    className="toc-input"
                                    type="date"
                                    value={hcRequireAfterDate}
                                    onChange={(e) => setHcRequireAfterDate(e.target.value)}
                                />
                            </FRow>
                        )}
                        <div className="toc-frow full">
                            <label className="toc-checkrow">
                                <input type="checkbox" checked={hcStack} onChange={(e) => setHcStack(e.target.checked)} />
                                <span className="cr-tx">
                                    <span className="cr-t">Stack with worked hours</span>
                                    <span className="cr-s">Holiday pay is added on top of hours actually worked that day.</span>
                                </span>
                            </label>
                        </div>
                    </FormModal>
                )}

                {/* ── Holiday policy modal ───────────────────────────────────── */}
                {hpModalOpen && (
                    <FormModal
                        icon={<CalendarCheck size={21} />}
                        title={editingHp ? "Edit holiday policy" : "New holiday policy"}
                        sub="A named set of holiday calendar entries."
                        onClose={() => setHpModalOpen(false)}
                        onSubmit={saveHp}
                        saveLabel={editingHp ? "Save policy" : "Create policy"}
                    >
                        <FRow label="Name" required full>
                            <input className="toc-input" value={hpName} onChange={(e) => setHpName(e.target.value)} placeholder="e.g. US Standard Holidays" />
                        </FRow>
                        <FRow label="Description" optional="optional" full>
                            <textarea value={hpDesc} onChange={(e) => setHpDesc(e.target.value)} placeholder="Who this set applies to…" />
                        </FRow>
                    </FormModal>
                )}

                {/* ── Add holiday to policy modal ────────────────────────────── */}
                {addHpHolModalOpen && (
                    <FormModal
                        icon={<Plus size={21} />}
                        title="Add holidays"
                        sub="Select calendar entries to include in this policy."
                        onClose={() => setAddHpHolModalOpen(false)}
                        onSubmit={addSelectedHolidays}
                        saveLabel="Add selected"
                    >
                        {(() => {
                            const avail = (allHcForPicker ?? [])
                                .filter(
                                    (h) =>
                                        h.active &&
                                        !hpDetail?.holidays.some((a) => a.holidayCalendarID === h.holidayCalendarID)
                                )
                                .sort((a, b) => a.holidayDate.localeCompare(b.holidayDate));
                            if (avail.length === 0) {
                                return (
                                    <div className="toc-frow full">
                                        <div className="toc-mini-empty">
                                            <CheckCircle size={28} />
                                            <div className="et">All holidays added</div>
                                            <div>Every active calendar entry is already in this policy.</div>
                                        </div>
                                    </div>
                                );
                            }
                            return avail.map((h) => {
                                const checked = pickerSelected.has(h.holidayCalendarID);
                                return (
                                    <div className="toc-frow full" key={h.holidayCalendarID}>
                                        <label className="toc-checkrow">
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() =>
                                                    setPickerSelected((prev) => {
                                                        const next = new Set(prev);
                                                        if (next.has(h.holidayCalendarID)) next.delete(h.holidayCalendarID);
                                                        else next.add(h.holidayCalendarID);
                                                        return next;
                                                    })
                                                }
                                            />
                                            <span className="cr-tx">
                                                <span className="cr-t">{h.name}</span>
                                                <span className="cr-s">
                                                    <span className="toc-mono">{fmtDate(h.holidayDate)}</span> ·{" "}
                                                    {hoursLabel(h.paidHoursIfNotWorked / 60)} · pay code {h.holidayPayCodeID}
                                                </span>
                                            </span>
                                        </label>
                                    </div>
                                );
                            });
                        })()}
                    </FormModal>
                )}

                {/* ── Confirm modal ──────────────────────────────────────────── */}
                {confirmState && <ConfirmModal state={confirmState} onClose={() => setConfirmState(null)} />}

                {/* ── Toast ──────────────────────────────────────────────────── */}
                {toast && (
                    <div className="toc-toast show">
                        <CheckCircle size={16} />
                        <span>{toast}</span>
                    </div>
                )}
            </div>
        </PageWrapper>
    );
};

export default TimeOffConfigurationPage;
